import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { getStickyMobilePricing } from '@theme/loop-subscription-sync';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isSyncingVariant = false;

  /** @type {string | null} */
  #pendingVariantId = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    if (this.#buyButtonsBlock) {
      this.#updateStickyVisibility(this.#buyButtonsBlock);
    }

    const { signal } = this.#abortController;
    this.#mobileMediaQuery.addEventListener('change', this.#handleMobileViewportChange, { signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('change', this.#handleStickyVariantChange, { signal });
    this.addEventListener('click', this.#handleBisTriggerClick, { signal });
    document.addEventListener('purchase-mode:change', this.#handlePurchaseModeChange, { signal });

    this.#getInitialQuantity();
    this.#resizeStickySelector();
    this.#syncMobileMainPadding();
    window.addEventListener('resize', this.#syncMobileMainPadding, { signal });
    requestAnimationFrame(() => this.#applyStickyCatalogPricing());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    document.documentElement.style.removeProperty('--sticky-atc-mobile-offset');
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsBlock = buyButtonsBlock;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      this.#updateStickyVisibility(buyButtonsBlock, entry);
    });

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;
          if (this.#isMobileViewport()) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            const rect = buyButtonsBlock.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByBottom = false;
              this.#showStickyBar();
            }
          }
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  #isMobileViewport() {
    return this.#mobileMediaQuery.matches;
  }

  #handleMobileViewportChange = () => {
    if (this.#buyButtonsBlock) {
      this.#updateStickyVisibility(this.#buyButtonsBlock);
    }
    this.#syncMobileMainPadding();
  };

  #syncMobileMainPadding = () => {
    if (!this.#isMobileViewport()) {
      document.documentElement.style.removeProperty('--sticky-atc-mobile-offset');
      return;
    }
    const { stickyBar } = this.refs;
    const height = stickyBar?.offsetHeight ?? 0;
    const offset = Math.max(height + 24, 140);
    document.documentElement.style.setProperty('--sticky-atc-mobile-offset', `${offset}px`);
  };

  /**
   * @param {HTMLElement} buyButtonsBlock
   * @param {IntersectionObserverEntry} [entry]
   */
  #updateStickyVisibility(buyButtonsBlock, entry) {
    const rect = buyButtonsBlock.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const mainAtcInView = entry?.isIntersecting ?? (rect.top < viewportHeight && rect.bottom > 0);

    if (mainAtcInView) {
      this.#hiddenByBottom = false;
      this.#hideStickyBar();
      return;
    }

    const isAboveViewport = rect.bottom < 0 || rect.top < 0;
    const isBelowFold = rect.top >= viewportHeight;

    if (this.#isMobileViewport()) {
      if (isAboveViewport || isBelowFold) {
        this.#showStickyBar();
      } else if (this.#isStuck) {
        this.#hideStickyBar();
      }
      return;
    }

    if (this.#hiddenByBottom) return;

    if (isAboveViewport) {
      this.#showStickyBar();
    } else if (this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    if (this.refs.addToCartButton.disabled) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.refs.addToCartButton.dataset.loading = 'true';
    this.refs.addToCartButton.disabled = true;
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.#syncVariantImage(variant);

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
      this.#syncStickySelector(variant.id);
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#syncVariantLoadingState(variant?.id ? String(variant.id) : null);

    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#resizeStickySelector();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#applyStickyCatalogPricing());
    });
  };

  /**
   * Sticky floater: locale catalog current/compare (from Liquid). Not Loop widget prices.
   */
  #applyStickyCatalogPricing() {
    const priceRoot = this.querySelector('[data-sticky-price]');
    if (!(priceRoot instanceof HTMLElement)) return;

    const currentEl = priceRoot.querySelector('[data-sticky-price-current]');
    const compareEl = priceRoot.querySelector('[data-sticky-price-compare]');
    const badgeEl = priceRoot.querySelector('[data-sticky-price-badge]');
    if (!(currentEl instanceof HTMLElement) || !(badgeEl instanceof HTMLElement)) return;

    const { currentToken, compareToken, discountPercent } = getStickyMobilePricing(priceRoot);

    if (currentToken) {
      currentEl.textContent = currentToken;
    }

    if (compareEl instanceof HTMLElement) {
      if (compareToken) {
        compareEl.textContent = compareToken;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (discountPercent != null && discountPercent > 0) {
      // badgeEl.textContent = `(${discountPercent}% off)`;
      badgeEl.hidden = false;
    } else {
      badgeEl.textContent = '';
      badgeEl.hidden = true;
    }
  }

  #applyStickyMobilePricing() {
    if (!this.#mobileMediaQuery.matches) return;
    this.#applyStickyCatalogPricing();
  }

  /**
   * @param {CustomEvent<{ mode?: string, label?: string, stickyMobilePricing?: { currentToken?: string, discountPercent?: number | null, isSubscription?: boolean } }>} event
   */
  #handlePurchaseModeChange = (event) => {
    const purchaseTypeEl = this.refs.purchaseType;
    if (!(purchaseTypeEl instanceof HTMLElement)) return;

    const label = event.detail?.label?.trim();
    if (!label) {
      purchaseTypeEl.hidden = true;
      purchaseTypeEl.textContent = '';
    } else {
      purchaseTypeEl.textContent = label;
      purchaseTypeEl.hidden = false;
    }

    this.#applyStickyCatalogPricing();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    this.#syncStickySelector(variantId);
  };

  /**
   * Handles sticky selector changes and forwards selection to main variant picker.
   * @param {Event} event
   */
  #handleStickyVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches('[data-variant-selector]')) return;
    if (this.#isSyncingVariant) return;

    const { value } = target;
    if (!value) return;
    this.#pendingVariantId = value;
    this.dataset.variantLoading = 'true';

    const synced = this.#syncMainVariantPicker(value);
    if (!synced) {
      this.#pendingVariantId = null;
      this.dataset.variantLoading = 'false';
    }
  };

  /**
   * Scrolls to the main buy-buttons block when the Klaviyo BIS trigger is clicked.
   * @param {Event} event
   */
  #handleBisTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('.klaviyo-bis-trigger')) return;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    buyButtonsBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' - ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Updates the sticky image with selected variant media when available.
   * @param {ProductVariant | null | undefined} variant
   */
  #syncVariantImage(variant) {
    if (!variant || !this.refs.productImage) return;

    const variantImageSrc = variant.featured_media?.preview_image?.src;
    if (variantImageSrc) {
      this.refs.productImage.src = variantImageSrc;
      this.refs.productImage.removeAttribute('srcset');
      this.refs.productImage.removeAttribute('sizes');
    }

    const imageAlt = variant.featured_media?.alt || variant.name || variant.title;
    if (imageAlt) {
      this.refs.productImage.alt = imageAlt;
    }
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    this.refs.addToCartButton.dataset.loading = 'false';
    this.refs.addToCartButton.disabled = this.dataset.variantAvailable !== 'true';
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    requestAnimationFrame(() => this.#syncMobileMainPadding());
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = this.dataset.variantAvailable === 'true';

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * Keeps sticky variant selector in sync with current variant.
   * @param {string | number} variantId
   */
  #syncStickySelector(variantId) {
    const selector = this.querySelector('[data-variant-selector]');
    if (!(selector instanceof HTMLSelectElement)) return;

    const normalizedVariantId = String(variantId);
    if (selector.value === normalizedVariantId) return;

    this.#isSyncingVariant = true;
    selector.value = normalizedVariantId;
    this.#isSyncingVariant = false;
    this.#resizeStickySelector();
  }

  /**
   * Scales the font-size of the sticky variant selector so the longest
   * option text fits within the select's available width on a single line.
   */
  #resizeStickySelector() {
    const selector = this.querySelector('[data-variant-selector]');
    if (!(selector instanceof HTMLSelectElement)) return;

    selector.style.fontSize = '';
    selector.style.width = '';

    const cs = getComputedStyle(selector);
    const baseFontSize = parseFloat(cs.fontSize);
    const paddingLeft = parseFloat(cs.paddingLeft) || 10;
    const paddingRight = parseFloat(cs.paddingRight) || 32;
    const availableWidth = selector.clientWidth - paddingLeft - paddingRight;
    if (availableWidth <= 0) return;

    const measurer = document.createElement('span');
    measurer.style.cssText =
      'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;';
    measurer.style.font = cs.font;
    measurer.style.letterSpacing = cs.letterSpacing;
    document.body.appendChild(measurer);

    let maxTextWidth = 0;
    for (const option of selector.options) {
      measurer.textContent = option.textContent?.trim() ?? '';
      if (measurer.offsetWidth > maxTextWidth) {
        maxTextWidth = measurer.offsetWidth;
      }
    }
    document.body.removeChild(measurer);

    if (maxTextWidth > availableWidth) {
      const minFontSize = 9;
      const scaled = baseFontSize * (availableWidth / maxTextWidth);
      selector.style.fontSize = `${Math.max(scaled, minFontSize)}px`;
    }
  }

  /**
   * Applies a sticky selection to the main variant picker.
   * @param {string} variantId
   */
  #syncMainVariantPicker(variantId) {
    const productId = this.dataset.productId;
    if (!productId) return false;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return false;

    const mainVariantPicker = sectionElement.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!(mainVariantPicker instanceof HTMLElement)) return false;

    const selectedRadio = mainVariantPicker.querySelector(`input[data-variant-id="${variantId}"]`);
    if (selectedRadio instanceof HTMLInputElement) {
      this.#isSyncingVariant = true;
      selectedRadio.checked = true;
      selectedRadio.dispatchEvent(new Event('change', { bubbles: true }));
      this.#isSyncingVariant = false;
      return true;
    }

    const selectedOption = mainVariantPicker.querySelector(`option[data-variant-id="${variantId}"]`);
    if (selectedOption instanceof HTMLOptionElement && selectedOption.parentElement instanceof HTMLSelectElement) {
      this.#isSyncingVariant = true;
      selectedOption.parentElement.value = selectedOption.value;
      selectedOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      this.#isSyncingVariant = false;
      return true;
    }
    return false;
  }

  /**
   * Clears sticky loading state once the expected variant has fully updated.
   * @param {string | null} updatedVariantId
   */
  #syncVariantLoadingState(updatedVariantId) {
    if (this.dataset.variantLoading !== 'true') return;
    if (this.#pendingVariantId && updatedVariantId && this.#pendingVariantId !== updatedVariantId) return;

    this.#pendingVariantId = null;
    requestAnimationFrame(() => {
      this.dataset.variantLoading = 'false';
    });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
